import {
  CURRENT_WEEK,
  QUEUED_COHORTS,
  SKILL_ROWS,
  type Coverage,
} from '@/demo/content/skillsMatrix'
import { KPIS, TIME_TO_BILLABLE_WEEKS } from '@/demo/content/talentPipeline'
import {
  HERO_PACK_ID,
  ROUTES as LIBRARY_ROUTES,
  TEMPLATE_PACKS,
  type TemplatePack,
} from '@/demo/content/templateLibrary'
import { MATCH_CANDIDATES, personById, projectById } from '@/demo/data'

/**
 * Surface 1.8 — Training center: the Competence Center read from the learner's
 * side. Every figure, label, route and sentence the page renders resolves
 * through this module, `@/demo/data`, or the three content modules this one is
 * downstream of; the component carries no literal.
 *
 * WHAT THE DECK GIVES: 11 weeks to first billable work (was 26), 88% still
 * here after a year, and six hours a week booked as non-billable learning
 * time. The first two already live on the talent pipeline (1.7) and are read
 * from there rather than re-typed — only the "was" figure and the weekly hours
 * are transcribed here, both guarded.
 *
 * WHAT IS AUTHORED HERE (once): the catalogue, the learner's path to the
 * migration badge, and the two courses she is enrolled in today. Everything
 * else is derived, so the page cannot contradict itself or its neighbours:
 *  - every course names a skill the matrix (1.5) already tracks, and shows the
 *    coverage band that skill sits in — the catalogue IS the matrix's gap list;
 *  - the two cohorts the matrix has queued (migration W38, QA W41) are read
 *    from `QUEUED_COHORTS`, seats included, never re-typed;
 *  - "taught with" resolves to a real pack and a real asset INSIDE that pack
 *    (guarded against `contains`), so no course teaches from material the
 *    library does not hold;
 *  - the path's course requirement takes its hours from the catalogue and its
 *    completed hours from the enrolment, so the header percentage and the
 *    course row below it are the same number seen twice;
 *  - the supervised hours still owed are the 8 h pairing the allocation screen
 *    (1.4) ranks her for — guarded against that match note.
 *
 * ONE-WORLD RESOLUTION: Arta Berisha is the growth move on the Van Dijk
 * migration (`MATCH_CANDIDATES`), carries "migration (assisted)" in the seed,
 * and her profile (1.2) books "Certified migration engineer" for October —
 * so this surface shows her BEFORE that course, finishing the foundations and
 * the supervised load that qualify her for it. Vlora Ahmeti's cohort 4 and the
 * cohorts running in the hub belong to the pipeline surface and are not
 * restated here.
 */

/** A run of prose in which some words are real destinations. */
export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'link'; label: string; href: string }

/* ── Routes (no href is built by hand in the component) ─────────────────── */

export const personHref = (personId: string): string => `/resources/people/${personId}`
export const BADGES_HREF = '/resources/badges'
export const PIPELINE_HREF = '/resources/pipeline'
export const CAPACITY_HREF = '/resources/capacity'
export const ALLOCATIONS_HREF = '/resources/allocations'
export const SKILLS_HREF = '/resources/skills'
export const TEMPLATES_HREF = '/library/templates'

/* ── Chrome ─────────────────────────────────────────────────────────────── */

export const TRAINING_CHROME = {
  moduleLabel: 'PX · Resources',
  breadcrumb: 'Growth › Training center',
  contextPill: 'MY PATH',
} as const

/* ── Deck figures, transcribed once ─────────────────────────────────────── */

const DECK = {
  /** How far the learner is along her badge path — guarded against the steps. */
  pathProgressPct: 64,
  /** Time to first billable work before the PX curriculum, in weeks. */
  timeToBillableWasWeeks: 26,
  /** Learning time every person on a path books as non-billable. */
  learningHoursPerWeek: 6,
} as const

if (DECK.timeToBillableWasWeeks <= TIME_TO_BILLABLE_WEEKS) {
  throw new Error(
    `The curriculum claims an improvement it does not have: ${TIME_TO_BILLABLE_WEEKS} weeks now against ${DECK.timeToBillableWasWeeks} before`
  )
}

const RETENTION_KPI = KPIS.find((kpi) => kpi.key === 'retention')
if (RETENTION_KPI === undefined) {
  throw new Error('The talent pipeline reports no retention figure — the panel cannot state it')
}

/* ── The badge ladder ───────────────────────────────────────────────────── */

/** Learner → Assisted → Certified → Lead: course, supervised delivery, sign-off. */
export const BADGE_LADDER = ['Learner', 'Assisted', 'Certified', 'Lead'] as const
export type BadgeLevel = (typeof BADGE_LADDER)[number]

/** Badges read as words, never as a mono-uppercase code. */
const badgeLabel = (level: BadgeLevel, subject: string): string => `${level} · ${subject}`

/* ── The catalogue ──────────────────────────────────────────────────────── */

export type Track = 'Consultant' | 'Engineer' | 'Developer' | 'Vertical' | 'Platform'

/** Seats in one Competence Center cohort — the room, not an ambition. */
export const COHORT_SEATS = 6

interface CourseSeed {
  key: string
  title: string
  track: Track
  /** The tracked group skill this course exists to close. */
  skillKey: string
  /** The library pack the course teaches from, and the asset inside it. */
  packId: string
  asset: string
  hours: number
  badgeLevel: BadgeLevel
  /** Defaults to the skill's own name when the badge carries no other subject. */
  badgeSubject?: string
  /** A cohort the skills matrix has already queued — weeks and seats come from it. */
  queueKey?: string
  /** Weeks out from today, for a cohort the matrix has not queued. */
  weeksOut?: number
  /** Seats already taken. Read from the queue when `queueKey` is set. */
  seatsTaken?: number
}

/**
 * Six courses. Each one is here because a tracked skill is thin or critical —
 * there is no course on this list that the matrix did not ask for.
 */
const COURSE_SEEDS: readonly CourseSeed[] = [
  {
    key: 'migration-foundations',
    title: 'Migration foundations · field mapping & loads',
    track: 'Engineer',
    skillKey: 'odoo-data-migration',
    packId: 'migration-toolkit',
    asset: 'Field maps',
    hours: 40,
    badgeLevel: 'Assisted',
    queueKey: 'odoo-data-migration',
  },
  {
    key: 'certified-migration-engineer',
    title: 'Certified migration engineer',
    track: 'Engineer',
    skillKey: 'odoo-data-migration',
    packId: 'migration-toolkit',
    asset: 'Load scripts',
    hours: 32,
    badgeLevel: 'Certified',
    weeksOut: 8,
    seatsTaken: 4,
  },
  {
    key: 'regression-essentials',
    title: 'Regression testing essentials',
    track: 'Engineer',
    skillKey: 'qa-regression',
    packId: HERO_PACK_ID,
    asset: 'Test sets',
    hours: 24,
    badgeLevel: 'Learner',
    queueKey: 'qa-regression',
  },
  {
    key: 'retail-data-models',
    title: 'Retail data models',
    track: 'Vertical',
    skillKey: 'retail-vertical',
    packId: HERO_PACK_ID,
    asset: 'Migration mappings',
    hours: 28,
    badgeLevel: 'Certified',
    badgeSubject: 'Retail data models',
    weeksOut: 4,
    seatsTaken: 3,
  },
  {
    key: 'integrations-api',
    title: 'Integrations & API development',
    track: 'Developer',
    skillKey: 'integrations-api',
    packId: 'migration-toolkit',
    asset: 'Validation rules',
    hours: 32,
    badgeLevel: 'Certified',
    weeksOut: 5,
    seatsTaken: 2,
  },
  {
    key: 'cutover-operations',
    title: 'Cutover & go-live operations',
    track: 'Platform',
    skillKey: 'cutover-infrastructure',
    packId: 'migration-toolkit',
    asset: 'Rehearsal runbook',
    hours: 16,
    badgeLevel: 'Assisted',
    weeksOut: 10,
    seatsTaken: 6,
  },
] as const

export interface CourseSkill {
  name: string
  coverage: Coverage
  href: string
}

export interface TaughtWith {
  label: string
  href: string
}

export interface CourseBadge {
  label: string
  href: string
}

export interface CourseCohort {
  /** e.g. "starts W38" — the matrix's own wording where the matrix owns it. */
  weekLabel: string
  seatsTaken: number
  seatsLeft: number
  seatsLabel: string
  full: boolean
  /** True when the skills matrix opened this cohort, not the training team. */
  fromMatrixQueue: boolean
}

export interface CourseRow {
  key: string
  title: string
  track: Track
  hours: number
  hoursLabel: string
  skill: CourseSkill
  taughtWith: TaughtWith
  badge: CourseBadge
  cohort: CourseCohort
}

const packById = (packId: string): TemplatePack => {
  const pack = TEMPLATE_PACKS.find((candidate) => candidate.id === packId)
  if (pack === undefined) throw new Error(`No library pack to teach from: ${packId}`)
  return pack
}

/** A course may only claim material the pack actually holds. */
const taughtWith = (seed: CourseSeed): TaughtWith => {
  const pack = packById(seed.packId)
  if (!pack.contains.includes(seed.asset)) {
    throw new Error(`${pack.name} holds no ${seed.asset} — "${seed.title}" cannot be taught with it`)
  }
  return {
    label: `${pack.name} ${pack.version} · ${seed.asset.toLowerCase()}`,
    href: pack.id === HERO_PACK_ID ? LIBRARY_ROUTES.templateDetail(pack.id) : TEMPLATES_HREF,
  }
}

const cohortOf = (seed: CourseSeed): CourseCohort => {
  const queued =
    seed.queueKey === undefined
      ? undefined
      : QUEUED_COHORTS.find((cohort) => cohort.key === seed.queueKey)
  if (seed.queueKey !== undefined && queued === undefined) {
    throw new Error(`The matrix queues no cohort for ${seed.queueKey}`)
  }
  const seatsTaken = queued?.people ?? seed.seatsTaken
  if (seatsTaken === undefined) {
    throw new Error(`No seats known for the next "${seed.title}" cohort`)
  }
  if (seatsTaken > COHORT_SEATS) {
    throw new Error(`The next "${seed.title}" cohort is over-booked: ${seatsTaken} of ${COHORT_SEATS}`)
  }
  if (queued === undefined && seed.weeksOut === undefined) {
    throw new Error(`No start week known for the next "${seed.title}" cohort`)
  }
  const seatsLeft = COHORT_SEATS - seatsTaken
  return {
    weekLabel: queued?.startWeekLabel ?? `starts W${CURRENT_WEEK + (seed.weeksOut ?? 0)}`,
    seatsTaken,
    seatsLeft,
    seatsLabel: seatsLeft === 0 ? 'waitlist' : `${seatsLeft} of ${COHORT_SEATS} seats left`,
    full: seatsLeft === 0,
    fromMatrixQueue: queued !== undefined,
  }
}

const toCourseRow = (seed: CourseSeed): CourseRow => {
  const skill = SKILL_ROWS.find((row) => row.key === seed.skillKey)
  if (skill === undefined) {
    throw new Error(`"${seed.title}" covers no tracked skill: ${seed.skillKey}`)
  }
  return {
    key: seed.key,
    title: seed.title,
    track: seed.track,
    hours: seed.hours,
    hoursLabel: `${seed.hours} h`,
    skill: { name: skill.name, coverage: skill.coverage, href: SKILLS_HREF },
    taughtWith: taughtWith(seed),
    badge: {
      label: badgeLabel(seed.badgeLevel, seed.badgeSubject ?? skill.name),
      href: BADGES_HREF,
    },
    cohort: cohortOf(seed),
  }
}

export const CATALOGUE: readonly CourseRow[] = COURSE_SEEDS.map(toCourseRow)

const courseByKey = (key: string): CourseRow => {
  const course = CATALOGUE.find((row) => row.key === key)
  if (course === undefined) throw new Error(`No course in the catalogue: ${key}`)
  return course
}

const COVERED_SKILLS = new Set(CATALOGUE.map((course) => course.skill.name)).size

/* ── Who this surface is for ────────────────────────────────────────────── */

const LEARNER_ID = 'arta-berisha'
const learner = personById(LEARNER_ID)

export const LEARNER = {
  id: learner.id,
  name: learner.name,
  initial: learner.initial,
  role: learner.role,
  href: personHref(learner.id),
  meta: `${learner.office} · joined ${learner.joined}`,
} as const

/* ── 1. My path ─────────────────────────────────────────────────────────── */

/** The course whose badge the path ends on — its title IS the target role. */
const TARGET_COURSE = courseByKey('certified-migration-engineer')

interface RequirementSeed {
  key: string
  label: string
  /** Hours-equivalent this step is worth on the path. */
  weightHours: number
  doneHours: number
}

export interface PathRequirement {
  key: string
  label: string
  weightHours: number
  doneHours: number
  reading: string
  complete: boolean
  started: boolean
}

/* ── 2. Enrolled now ────────────────────────────────────────────────────── */

interface EnrolmentSeed {
  courseKey: string
  hoursDone: number
}

/** The two courses running for her today. Hours come from the catalogue. */
const ENROLMENT_SEEDS: readonly EnrolmentSeed[] = [
  { courseKey: 'migration-foundations', hoursDone: 30 },
  { courseKey: 'regression-essentials', hoursDone: 14 },
] as const

export interface Enrolment {
  key: string
  title: string
  taughtWith: TaughtWith
  badge: CourseBadge
  hours: number
  hoursDone: number
  reading: string
  progressPct: number
  /** True when this course is a step on the badge path above. */
  onPath: boolean
}

const PATH_COURSE_KEY = 'migration-foundations'

export const ENROLMENTS: readonly Enrolment[] = ENROLMENT_SEEDS.map((seed) => {
  const course = courseByKey(seed.courseKey)
  if (seed.hoursDone > course.hours) {
    throw new Error(`"${course.title}" has more hours done than it holds`)
  }
  return {
    key: course.key,
    title: course.title,
    taughtWith: course.taughtWith,
    badge: course.badge,
    hours: course.hours,
    hoursDone: seed.hoursDone,
    reading: `${seed.hoursDone} of ${course.hours} h`,
    progressPct: Math.round((seed.hoursDone / course.hours) * 100),
    onPath: course.key === PATH_COURSE_KEY,
  }
})

const PATH_ENROLMENT = ENROLMENTS.find((enrolment) => enrolment.key === PATH_COURSE_KEY)
if (PATH_ENROLMENT === undefined) {
  throw new Error(`The path's course is not among the enrolments: ${PATH_COURSE_KEY}`)
}

/** Supervised hours the badge asks for, and how many are already delivered. */
const SUPERVISED_HOURS = 32
const SUPERVISED_DONE = 24

const REQUIREMENT_SEEDS: readonly RequirementSeed[] = [
  {
    key: 'assessment',
    label: 'Competence Center assessment',
    weightHours: 10,
    doneHours: 10,
  },
  {
    key: 'course',
    label: PATH_ENROLMENT.title,
    weightHours: PATH_ENROLMENT.hours,
    doneHours: PATH_ENROLMENT.hoursDone,
  },
  {
    key: 'supervised',
    label: 'Supervised delivery on a live migration',
    weightHours: SUPERVISED_HOURS,
    doneHours: SUPERVISED_DONE,
  },
  {
    key: 'sign-off',
    label: 'Expert sign-off & badge review',
    weightHours: 18,
    doneHours: 0,
  },
] as const

export const PATH_REQUIREMENTS: readonly PathRequirement[] = REQUIREMENT_SEEDS.map((seed) => ({
  key: seed.key,
  label: seed.label,
  weightHours: seed.weightHours,
  doneHours: seed.doneHours,
  reading: `${seed.doneHours} of ${seed.weightHours} h`,
  complete: seed.doneHours === seed.weightHours,
  started: seed.doneHours > 0,
}))

const sumHours = (pick: (requirement: PathRequirement) => number): number =>
  PATH_REQUIREMENTS.reduce((total, requirement) => total + pick(requirement), 0)

const PATH_TOTAL_HOURS = sumHours((requirement) => requirement.weightHours)
const PATH_DONE_HOURS = sumHours((requirement) => requirement.doneHours)
const PATH_PROGRESS_PCT = Math.round((PATH_DONE_HOURS / PATH_TOTAL_HOURS) * 100)

if (PATH_PROGRESS_PCT !== DECK.pathProgressPct) {
  throw new Error(
    `The path steps read ${PATH_PROGRESS_PCT}% but the deck states ${DECK.pathProgressPct}%`
  )
}

/** What is still owed on the one step a project can move — the pairing hours. */
const SUPERVISED_REMAINING = SUPERVISED_HOURS - SUPERVISED_DONE

const LEARNER_MATCH = MATCH_CANDIDATES.find((candidate) => candidate.personId === LEARNER_ID)
if (LEARNER_MATCH === undefined) {
  throw new Error(`${learner.name} is not ranked on the open task — the milestone has no evidence`)
}
if (!LEARNER_MATCH.impact.includes(`${SUPERVISED_REMAINING} h`)) {
  throw new Error(
    `The path owes ${SUPERVISED_REMAINING} h of pairing but the allocation match reads "${LEARNER_MATCH.impact}"`
  )
}

const LEARNER_PROJECT =
  learner.currentProject === null ? null : projectById(learner.currentProject)
if (LEARNER_PROJECT === null) {
  throw new Error(`${learner.name} is on no project — the supervised load cannot be named`)
}

export const MY_PATH = {
  label: 'MY PATH',
  fromRole: learner.role,
  toRole: TARGET_COURSE.title,
  badge: TARGET_COURSE.badge,
  progressPct: PATH_PROGRESS_PCT,
  progressLabel: `${PATH_PROGRESS_PCT}%`,
  progressReading: `${PATH_DONE_HOURS} of ${PATH_TOTAL_HOURS} h of the badge met`,
  requirementsLabel: 'What the badge asks for',
  milestoneLabel: 'Next',
  /** Plain words, and the words that matter are destinations. */
  milestone: [
    {
      kind: 'text',
      text: `${SUPERVISED_REMAINING} hours of supervised migration work stand between her and the badge. PX ranks her as the ${LEARNER_MATCH.tag.toLowerCase()} on the `,
    },
    { kind: 'link', label: `${LEARNER_PROJECT.client} migration`, href: ALLOCATIONS_HREF },
    {
      kind: 'text',
      text: ` — the match note reads “${LEARNER_MATCH.impact}”. A manager decides that booking; the badge follows the delivered work, not the course.`,
    },
  ] satisfies readonly Segment[],
} as const

/* ── 2. Enrolled-now copy ───────────────────────────────────────────────── */

export const ENROLLED_SECTION = {
  label: 'ENROLLED NOW',
  caption: `The ${ENROLMENTS.length} courses ${learner.name} is sitting today, with the hours done and the badge each awards.`,
  taughtWithPrefix: 'taught with',
  awardsPrefix: 'awards',
  onPathTag: 'On my path',
} as const

/* ── 3. How courses are built ───────────────────────────────────────────── */

export interface BuiltStat {
  key: string
  label: string
  figure: string
  qualifier: readonly Segment[]
}

const PACKS_TAUGHT_FROM = new Set(CATALOGUE.map((course) => course.taughtWith.label.split(' · ')[0]))
  .size

export const HOW_BUILT = {
  label: 'HOW COURSES ARE BUILT',
  stats: [
    {
      key: 'time-to-billable',
      label: 'To first billable work',
      figure: `${TIME_TO_BILLABLE_WEEKS} weeks`,
      qualifier: [
        { kind: 'text', text: `was ${DECK.timeToBillableWasWeeks} before this curriculum — the ` },
        { kind: 'link', label: 'talent pipeline', href: PIPELINE_HREF },
        { kind: 'text', text: ' counts it end to end' },
      ],
    },
    {
      key: 'retention',
      label: RETENTION_KPI.label,
      figure: RETENTION_KPI.figure,
      qualifier: [
        {
          kind: 'text',
          text: 'of the people who reached billable work are still here a year on',
        },
      ],
    },
    {
      key: 'library-taught',
      label: 'Taught from the library',
      figure: `${CATALOGUE.length} of ${CATALOGUE.length}`,
      qualifier: [
        { kind: 'text', text: `courses teach from ${PACKS_TAUGHT_FROM} packs of anonymised ` },
        { kind: 'link', label: 'delivered project material', href: TEMPLATES_HREF },
        { kind: 'text', text: ', not slideware' },
      ],
    },
  ] satisfies readonly BuiltStat[],
} as const

/* ── 4. Time given to learning ──────────────────────────────────────────── */

export const LEARNING_TIME = {
  label: 'TIME GIVEN TO LEARNING',
  figure: `${DECK.learningHoursPerWeek} h`,
  figureQualifier: 'a week, for everyone on a path',
  prose: [
    { kind: 'text', text: 'Those hours are booked as non-billable in the ' },
    { kind: 'link', label: 'capacity forecast', href: CAPACITY_HREF },
    {
      kind: 'text',
      text: ' — learning time is a booking like any other, so a busy project cannot quietly take it back.',
    },
  ] satisfies readonly Segment[],
} as const

/* ── 5. Catalogue copy ──────────────────────────────────────────────────── */

export const CATALOGUE_SECTION = {
  label: 'CATALOGUE',
  caption: `The ${CATALOGUE.length} courses open across ${COVERED_SKILLS} of the ${SKILL_ROWS.length} tracked skills, with the material each is taught from, the hours it takes, the badge it awards and the next cohort.`,
  columns: {
    course: 'Course',
    track: 'Track',
    taughtWith: 'Taught with',
    hours: 'Hours',
    badge: 'Badge awarded',
    cohort: 'Next cohort',
  },
  coversPrefix: 'covers',
  matrixLinkTitle: 'Open the skills matrix',
  queuedTag: 'Opened by the matrix',
  fullTag: 'Waitlist',
} as const

/** Coverage band → what it means for the course that closes it. */
export const COVERAGE_MEANING: Record<Coverage, string> = {
  Critical: 'one holder today',
  Thin: 'no depth today',
  Healthy: 'covered today',
}

/* ── 6. Page copy ───────────────────────────────────────────────────────── */

const PAYROLL_SKILL = SKILL_ROWS.find((row) => row.key === 'payroll-be')
if (PAYROLL_SKILL === undefined) {
  throw new Error('No payroll skill on the matrix — the density note cannot state what is missing')
}

export const TRAINING_COPY = {
  title: 'Training center',
  lede: `What ${learner.name} is learning, what she can enrol in, and the badge each course awards.`,
  densityNote: [
    {
      kind: 'text',
      text: `The catalogue is short because it is built from the `,
    },
    { kind: 'link', label: 'skills matrix', href: SKILLS_HREF },
    {
      kind: 'text',
      text: `, not from a wish list: ${CATALOGUE.length} courses across ${COVERED_SKILLS} of the ${SKILL_ROWS.length} tracked skills, and nothing here that a cohort cannot close. ${PAYROLL_SKILL.name} is critical on that matrix and is absent on purpose — it is certified externally, so it opens a `,
    },
    { kind: 'link', label: 'role', href: PIPELINE_HREF },
    {
      kind: 'text',
      text: ' rather than a cohort. Completed-course history is withheld too: a finished course is evidence, and it lives on the ',
    },
    { kind: 'link', label: 'person profile', href: LEARNER.href },
    { kind: 'text', text: ' beside the delivery that proved it.' },
  ] satisfies readonly Segment[],
} as const
